import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, categories } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen flex flex-col bg-background-50">
          <Navbar />
          <main className="flex-1 flex items-center justify-center pt-16">
            <div className="text-center">
              <h1 className="font-heading text-2xl text-foreground-900 mb-3">文章未找到</h1>
              <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  if (relatedArticles.length < 3) {
    const otherArticles = articles
      .filter((a) => a.category !== article.category && a.slug !== article.slug)
      .slice(0, 3 - relatedArticles.length);
    relatedArticles.push(...otherArticles);
  }

  const categoryName = category?.name || article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '淫乱小镇',
      url: 'https://hzhgjs.cn',
    },
    url: `https://hzhgjs.cn/article/${article.slug}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hzhgjs.cn/article/${article.slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />

        <main className="flex-1 pt-20">
          <article className="max-w-3xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>

            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.views.toLocaleString()} 阅读
                </span>
              </div>
            </header>

            {/* Cover Image */}
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 淫乱小镇`}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>

            {/* Article Content */}
            <div className="article-content mb-10">
              {article.content.map((block, index) => {
                if (block.type === 'h2') {
                  return (
                    <h2 key={index}>{block.text}</h2>
                  );
                }
                return (
                  <p key={index}>{block.text}</p>
                );
              })}
            </div>

            {/* Article Footer */}
            <div className="border-t border-background-200/70 pt-6 mb-10">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-4 text-sm text-foreground-500">
                  <span>作者：{article.author}</span>
                  <span>发布于 {article.date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500">分享：</span>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link"></i>
                  </button>
                </div>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="bg-background-100/70 py-10 md:py-14">
              <div className="max-w-6xl mx-auto px-4 md:px-6">
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
                  <strong>相关推荐</strong>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.slug} {...ra} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}